import React, { useContext, useRef, useState } from "react"
import * as S from "./trial-contact.styles"
import {
  FormControlLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material"
import MailOutlineIcon from "@mui/icons-material/MailOutline"
import CustomButton from "../custom-button/custom-button.component"
import PersonOutlineIcon from "@mui/icons-material/PersonOutline"
import PhoneIcon from "@mui/icons-material/Phone"
import Grid from "@mui/material/Grid"
import CityIcon from "../../assets/icons/city.svg"
import StateIcon from "../../assets/icons/state.svg"
import CompanyIcon from "../../assets/icons/company.svg"
import WebsiteIcon from "../../assets/icons/website.svg"
import ReCAPTCHA from "react-google-recaptcha"
import { gtagSubmitForm } from "../../gtag-utils"
import { colorsContext } from "../../provider"
import { subscribeNewsletter } from "../../utils"
import { FORM_SUBMIT_ERRORS } from "../../utils/constants"
import { FormProvider, useForm } from "react-hook-form"

const TrialContact = ({
  question,
  hasSpanishContent,
  tag,
  redirectUrl,
  sendToMailchimp,
}) => {
  const [value, setValue] = useState("no")
  const [error, setError] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const { isEnglish } = useContext(colorsContext)
  const methods = useForm()
  const captchaSiteKey = process.env.GATSBY_CAPTCHA_SITE_KEY
  const recaptchaRef = useRef()
  const { watch } = methods

  const watchFields = watch()

  const handleChange = event => {
    setValue(event.target.value)
  }
  const handleCaptchaChange = () => {
    setError(false)
  }
  const handleSubmit = async evt => {
    evt.preventDefault()
    const captchaVerification = recaptchaRef.current.getValue()
    if (!captchaVerification) {
      setError(
        hasSpanishContent
          ? isEnglish
            ? FORM_SUBMIT_ERRORS.captchaError
            : FORM_SUBMIT_ERRORS.captchaErrorSpanish
          : FORM_SUBMIT_ERRORS.captchaError
      )
      setIsLoading(false)
      return false
    }
    if (sendToMailchimp) {
      try {
        const res = await subscribeNewsletter({
          email: watchFields.email,
          first_name: watchFields.first_name,
          last_name: watchFields.last_name,
          phone: watchFields.phone,
          tag: tag,
        })
        if (res?.error) {
          setError(FORM_SUBMIT_ERRORS.mailChimpError)
          setIsLoading(false)
          return false
        }
        setIsLoading(false)
        gtagSubmitForm(tag)
        setError("")
        evt.target.submit()
      } catch (err) {
        console.log(err)
      }
    } else {
      gtagSubmitForm(tag)
      evt.target.submit()
    }
  }

  return (
    <FormProvider {...methods}>
      <S.Form
        action="https://webto.salesforce.com/servlet/servlet.WebToLead?encoding=UTF-8"
        method="POST"
        onSubmit={handleSubmit}
      >
        <input type="hidden" name="oid" value="00D30000000mGwB" />
        <input type="hidden" name="retURL" value={redirectUrl} />
        <input type="hidden" name="lead_source" value={tag} />
        <input type="hidden" name="recordType" value="01230000000qdAj" />

        <S.GridContainer container spacing={2}>
          <Grid item xs={12}>
            <S.CustomField
              placeholder={
                hasSpanishContent
                  ? isEnglish
                    ? "First Name*"
                    : "Nombre*"
                  : "First Name*"
              }
              id="first_name"
              required
              name="first_name"
              fullWidth
              variant="outlined"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="start">
                    <PersonOutlineIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <S.CustomField
              color="secondary"
              id="last_name"
              required
              maxLength="40"
              name="last_name"
              placeholder={
                hasSpanishContent
                  ? isEnglish
                    ? "Last Name*"
                    : "Apellido*"
                  : "Last Name*"
              }
              fullWidth
              variant="outlined"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="start">
                    <PersonOutlineIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <S.CustomField
              type="email"
              id="email"
              name="email"
              required
              size="20"
              placeholder="Email*"
              fullWidth
              variant="outlined"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="start">
                    <MailOutlineIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <S.CustomField
              placeholder={
                hasSpanishContent
                  ? isEnglish
                    ? "Phone*"
                    : "Teléfono*"
                  : "Phone*"
              }
              id="phone"
              size="20"
              name="phone"
              required
              fullWidth
              type="number"
              variant="outlined"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <S.CustomField
              color="secondary"
              id="company"
              maxLength="40"
              name="company"
              placeholder={
                hasSpanishContent
                  ? isEnglish
                    ? "Company"
                    : "Compañía"
                  : "Company"
              }
              fullWidth
              variant="outlined"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="start">
                    <CompanyIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <S.CustomField
              color="secondary"
              id="city"
              maxLength="40"
              name="city"
              placeholder={
                hasSpanishContent ? (isEnglish ? "City" : "Ciudad") : "City"
              }
              fullWidth
              variant="outlined"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="start">
                    <CityIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <S.CustomField
              color="secondary"
              id="state"
              maxLength="20"
              name="state"
              placeholder={
                hasSpanishContent
                  ? isEnglish
                    ? "State/Province"
                    : "Estado/Provincia"
                  : "State/Province"
              }
              fullWidth
              variant="outlined"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="start">
                    <StateIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <S.CustomField
              color="secondary"
              id="url"
              maxLength="80"
              name="url"
              placeholder={
                hasSpanishContent
                  ? isEnglish
                    ? "Website"
                    : "Sitio Web"
                  : "Website"
              }
              fullWidth
              variant="outlined"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="start">
                    <WebsiteIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12}>
            {question && (
              <>
                <S.Question>{question}</S.Question>
                <S.Control>
                  <RadioGroup
                    aria-label="c3 partner"
                    name="partner"
                    value={value}
                    onChange={handleChange}
                  >
                    {value === "yes" ? (
                      <S.CustomField
                        color="secondary"
                        id="00N4V00000DTrss"
                        maxLength="80"
                        name="00N4V00000DTrss"
                        placeholder="Partner Name"
                        fullWidth
                        variant="outlined"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="start">
                              <PersonOutlineIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    ) : (
                      <FormControlLabel
                        value="yes"
                        control={<Radio />}
                        label="Yes"
                      />
                    )}
                    <FormControlLabel
                      value="no"
                      control={<Radio />}
                      label="No"
                    />
                  </RadioGroup>
                </S.Control>
              </>
            )}
            <S.CaptchaContainer>
              <ReCAPTCHA
                sitekey={captchaSiteKey}
                size="normal"
                render="explicit"
                onChange={handleCaptchaChange}
                className="g-recaptcha"
                ref={recaptchaRef}
              />
              {error && (
                <Typography style={{ color: "#F54343" }}>{error}</Typography>
              )}
            </S.CaptchaContainer>
          </Grid>
        </S.GridContainer>
        <S.ButtonWrapper>
          <CustomButton fullWidth type="submit" loading={isLoading}>
            {hasSpanishContent ? (isEnglish ? "Submit" : "Enviar") : "Submit"}
          </CustomButton>
        </S.ButtonWrapper>
      </S.Form>
    </FormProvider>
  )
}

export default TrialContact
